import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Trash2 } from "lucide-react";
import { variantOptionsLabel } from "@KassaroAB/shared/catalog/variantOptions";
import { BarcodeType } from "@KassaroAB/shared/types/enums/BarcodeType";
import type { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { ExtraProductField } from "@KassaroAB/shared/types/extraProductField";
import type {
  ProductVariantId,
  StockLocationId,
} from "@KassaroAB/shared/types/ids";
import type { Money } from "@KassaroAB/shared/types/money";
import type { ProductVariant } from "@KassaroAB/shared/types/productVariant";
import type {
  RestStockPolicy,
  StockPolicyAssignment,
} from "@KassaroAB/shared/types/stockPolicy";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { borderRadius, spacing } from "@styling/sizing";
import { Button, IconButton } from "../../../components/Button";
import { SelectInput } from "../../../components/SelectInput";
import { formatMoneyAmountInputValue } from "../../../utils/parseMoneyAmountInput";
import {
  CellInput,
  CellSelect,
  MoneyTableCell,
  ReadOnlyFieldset,
} from "./MoneyTableCell";
import type { ProductVariantStock } from "./useProductVariantStock";

type Tab = "price" | "codes" | "stock" | "policy";

const NO_POLICY = "none";
const VARIES = "";
const CUSTOM_LEVELS = "custom";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x1}px;
  flex-wrap: wrap;
`;

const Tabs = styled.div`
  display: flex;
  gap: ${spacing.x05}px;
  padding: 2px;
  border-radius: ${borderRadius.regular}px;
  background-color: ${colors.backgroundDark};
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: ${spacing.x05}px ${spacing.x2}px;
  border: none;
  border-radius: ${borderRadius.small}px;
  background-color: ${({ $active }) =>
    $active ? colors.backgroundLight : "transparent"};
  color: ${({ $active }) => ($active ? colors.text : colors.textMuted)};
  font-size: ${fontSizes.medium}px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
`;

const TableScroll = styled.div`
  margin-top: ${spacing.x1}px;
  overflow-x: auto;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  background-color: ${colors.backgroundLight};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${fontSizes.medium}px;
  font-variant-numeric: tabular-nums;

  th,
  td {
    padding: ${spacing.x05}px ${spacing.x1}px;
    border-bottom: 1px solid ${colors.border};
    text-align: left;
    white-space: nowrap;
  }

  th {
    font-size: ${fontSizes.small}px;
    font-weight: 600;
    color: ${colors.textMuted};
    background-color: ${colors.background};
  }

  tr:last-child td {
    border-bottom: none;
  }

  input,
  select {
    font-family: inherit;
  }

  th[data-numeric] {
    text-align: right;
  }

  td[data-numeric] {
    width: 140px;

    input {
      text-align: right;
    }
  }
`;

const HeaderLabel = styled.div`
  font-weight: 400;
  color: ${colors.info};
`;

const AllRow = styled.tr`
  td {
    background-color: ${colors.background};
    font-weight: 600;
  }

  td:first-child {
    padding-left: ${spacing.x2 + 1}px;
  }
`;

const NameInput = styled(CellInput)`
  min-width: 160px;

  &::placeholder {
    color: ${colors.text};
  }
`;

const Hint = styled.p`
  margin: ${spacing.x1}px 0 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const Empty = styled.p`
  margin: ${spacing.x1}px 0 0;
  color: ${colors.textMuted};
`;

const LocationPicker = styled.div`
  width: 320px;
  margin-top: ${spacing.x1}px;
`;

interface Column {
  id: string;
  header: string;
  label?: string;
  numeric?: boolean;
  cell: (variantIds: ProductVariantId[], isAll: boolean) => ReactNode;
}

export interface ProductVariantsParent {
  unitPrice: Money;
  takeawayPrice: Money | null;
  purchasePrice: Money | null;
  barcodeCode: string;
  barcodeType: BarcodeType;
  articleNumber: string;
}

interface ProductVariantsTableProps {
  variants: ProductVariant[];
  onVariantsChange: (variants: ProductVariant[]) => void;
  parent: ProductVariantsParent;
  currency: Currency;
  extraFields: ExtraProductField[];
  showBarcode: boolean;
  showTakeawayPrice: boolean;
  showPurchasePrice: boolean;
  showStock: boolean;
  showPolicies: boolean;
  stockPolicies: RestStockPolicy[];
  stock: ProductVariantStock;
  onManage: () => void;
  chain?: ProductVariantsChain;
}

export interface ProductVariantsChain {
  isLocked: (field: string | null) => boolean;
  renderCell: (
    variantId: ProductVariantId,
    columnId: string,
    content: ReactNode,
  ) => ReactNode;
}

const sameValue = <T,>(
  values: T[],
): { value: T | undefined; mixed: boolean } => {
  const first = JSON.stringify(values[0] ?? null);
  const mixed = values.some((value) => JSON.stringify(value ?? null) !== first);
  return { value: mixed ? undefined : values[0], mixed };
};

const parseQuantity = (value: string): number | null => {
  const trimmed = value.trim().replace(",", ".");
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.round(parsed * 1000) / 1000 : null;
};

export const ProductVariantsTable = ({
  variants,
  onVariantsChange,
  parent,
  currency,
  extraFields,
  showBarcode,
  showTakeawayPrice,
  showPurchasePrice,
  showStock,
  showPolicies,
  stockPolicies,
  stock,
  onManage,
  chain,
}: ProductVariantsTableProps) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("price");
  const [policyLocationId, setPolicyLocationId] =
    useState<StockLocationId | null>(null);
  const policyLocation =
    stock.locations.find((location) => location.id === policyLocationId) ??
    stock.locations[0];

  const tabs: { value: Tab; label: string }[] = [
    { value: "price", label: t("productForm.variantsTable.price") },
    { value: "codes", label: t("productForm.variantsTable.codes") },
    ...(showStock
      ? [
          {
            value: "stock" as const,
            label: t("productForm.variantsTable.stock"),
          },
        ]
      : []),
    ...(showPolicies
      ? [
          {
            value: "policy" as const,
            label: t("productForm.variantsTable.policy"),
          },
        ]
      : []),
  ];
  const activeTab = tabs.some((item) => item.value === tab) ? tab : "price";

  const variantsById = new Map(
    variants.map((variant) => [variant.id, variant]),
  );
  const variantsOf = (ids: ProductVariantId[]) =>
    ids.flatMap((id) => variantsById.get(id) ?? []);
  const updateVariants = (
    ids: ProductVariantId[],
    update: (variant: ProductVariant) => ProductVariant,
  ) => {
    const idSet = new Set(ids);
    onVariantsChange(
      variants.map((variant) =>
        idSet.has(variant.id) ? update(variant) : variant,
      ),
    );
  };
  const extraFieldKeys = extraFields.map((field) => field.key);
  const variesPlaceholder = t("productForm.variantsTable.varies");

  const moneyColumn = (
    id: string,
    header: string,
    own: (variant: ProductVariant) => Money | null | undefined,
    inherited: Money | null,
    set: (variant: ProductVariant, value: Money | null) => ProductVariant,
  ): Column => ({
    id,
    header,
    numeric: true,
    cell: (ids, isAll) => {
      const { value, mixed } = sameValue(
        variantsOf(ids).map((variant) => own(variant) ?? null),
      );
      return (
        <MoneyTableCell
          amountMinor={value?.amount}
          placeholder={
            isAll && mixed
              ? variesPlaceholder
              : inherited
                ? formatMoneyAmountInputValue(inherited.amount)
                : undefined
          }
          allowEmpty
          onCommit={(minor) =>
            updateVariants(ids, (variant) =>
              set(
                variant,
                minor === null || minor === inherited?.amount
                  ? null
                  : { amount: minor, currency },
              ),
            )
          }
        />
      );
    },
  });

  const textColumn = (
    id: string,
    header: string,
    own: (variant: ProductVariant) => string | null | undefined,
    inherited: string,
    set: (variant: ProductVariant, value: string | null) => ProductVariant,
  ): Column => ({
    id,
    header,
    cell: (ids, isAll) => {
      const { value, mixed } = sameValue(
        variantsOf(ids).map((variant) => own(variant) ?? null),
      );
      return (
        <CellInput
          value={value ?? ""}
          placeholder={isAll && mixed ? variesPlaceholder : inherited}
          onChange={(event) =>
            updateVariants(ids, (variant) =>
              set(
                variant,
                event.target.value.trim() ? event.target.value : null,
              ),
            )
          }
        />
      );
    },
  });

  const quantityInput = (
    ids: (ProductVariantId | null)[],
    isAll: boolean,
    get: (variantId: ProductVariantId | null) => number | null,
    set: (
      variantIds: (ProductVariantId | null)[],
      value: number | null,
    ) => void,
  ) => {
    const { value, mixed } = sameValue(ids.map(get));
    return (
      <CellInput
        type="number"
        min={0}
        value={value ?? ""}
        disabled={stock.isLoading}
        placeholder={isAll && mixed ? variesPlaceholder : undefined}
        onChange={(event) => set(ids, parseQuantity(event.target.value))}
      />
    );
  };

  const priceColumns = (): Column[] => [
    moneyColumn(
      "unitPrice",
      t("productForm.unitPrice"),
      (variant) => variant.unitPrice,
      parent.unitPrice,
      (variant, unitPrice) => ({ ...variant, unitPrice }),
    ),
    ...(showTakeawayPrice
      ? [
          moneyColumn(
            "takeawayPrice",
            t("productForm.takeawayPrice"),
            (variant) => variant.takeawayPrice,
            parent.takeawayPrice,
            (variant, takeawayPrice) => ({ ...variant, takeawayPrice }),
          ),
        ]
      : []),
    ...(showPurchasePrice
      ? [
          moneyColumn(
            "purchasePrice",
            t("productForm.purchasePrice"),
            (variant) => variant.purchasePrice,
            parent.purchasePrice,
            (variant, purchasePrice) => ({ ...variant, purchasePrice }),
          ),
        ]
      : []),
  ];

  const codeColumns = (): Column[] => [
    ...(showBarcode
      ? [
          textColumn(
            "barcode",
            t("productForm.barcodeCode"),
            (variant) => variant.barcode?.code,
            parent.barcodeCode,
            (variant, code) => ({
              ...variant,
              barcode: code
                ? {
                    code,
                    type: variant.barcode?.type ?? parent.barcodeType,
                    format: variant.barcode?.format ?? null,
                  }
                : undefined,
            }),
          ),
        ]
      : []),
    textColumn(
      "articleNumber",
      t("productForm.articleNumber"),
      (variant) => variant.articleNumber,
      parent.articleNumber,
      (variant, articleNumber) => ({ ...variant, articleNumber }),
    ),
  ];

  const stockColumns = (): Column[] =>
    stock.locations.length > 0
      ? stock.locations.map((location) => ({
          id: `stock-${location.id}`,
          header: location.name,
          label: location.label,
          numeric: true,
          cell: (ids, isAll) =>
            quantityInput(
              ids,
              isAll,
              (variantId) => stock.getQuantity(location.id, variantId),
              (variantIds, quantity) =>
                stock.setQuantity(location.id, variantIds, quantity),
            ),
        }))
      : [
          {
            id: "stockCount",
            header: t("productForm.stockCount"),
            numeric: true,
            cell: (ids, isAll) =>
              quantityInput(
                ids,
                isAll,
                (variantId) =>
                  (variantId ? variantsById.get(variantId) : undefined)
                    ?.stockCount ?? null,
                (variantIds, stockCount) =>
                  updateVariants(
                    variantIds.filter((id): id is ProductVariantId => !!id),
                    (variant) => ({ ...variant, stockCount }),
                  ),
              ),
          },
        ];

  const getPolicy = (variantId: ProductVariantId) =>
    policyLocation
      ? stock.getPolicy(policyLocation.id, variantId)
      : (variantsById.get(variantId)?.stockPolicy ?? null);
  const setPolicy = (
    ids: ProductVariantId[],
    update: (
      current: StockPolicyAssignment | null,
    ) => StockPolicyAssignment | null,
  ) =>
    policyLocation
      ? stock.setPolicy(policyLocation.id, ids, update)
      : updateVariants(ids, (variant) => ({
          ...variant,
          stockPolicy: update(variant.stockPolicy ?? null),
        }));
  const policyOf = (assignment: StockPolicyAssignment | null) =>
    assignment?.policyId
      ? stockPolicies.find((policy) => policy.id === assignment.policyId)
      : undefined;

  const levelColumn = (level: "min" | "max", header: string): Column => ({
    id: `policy-${level}`,
    header,
    numeric: true,
    cell: (ids, isAll) => {
      const assignments = ids.map(getPolicy);
      const { value: own, mixed } = sameValue(
        assignments.map((assignment) => assignment?.[level] ?? null),
      );
      const fromPolicy = sameValue(
        assignments.map((assignment) => policyOf(assignment)?.[level] ?? null),
      ).value;
      return (
        <CellInput
          type="number"
          min={0}
          value={own ?? ""}
          disabled={stock.isLoading}
          placeholder={
            isAll && mixed
              ? variesPlaceholder
              : fromPolicy !== null && fromPolicy !== undefined
                ? String(fromPolicy)
                : t("productForm.notSet")
          }
          onChange={(event) => {
            const parsed = parseQuantity(event.target.value);
            const value =
              parsed === null ? null : Math.max(0, Math.round(parsed));
            setPolicy(ids, (assignment) =>
              !assignment && value === null
                ? null
                : {
                    policyId: assignment?.policyId ?? null,
                    min: assignment?.min ?? null,
                    max: assignment?.max ?? null,
                    [level]: value,
                  },
            );
          }}
        />
      );
    },
  });

  const policyColumns = (): Column[] => [
    {
      id: "policy",
      header: t("productForm.variantsTable.policy"),
      cell: (ids, isAll) => {
        const { value, mixed } = sameValue(
          ids.map((id) => {
            const assignment = getPolicy(id);
            return assignment
              ? (assignment.policyId ?? CUSTOM_LEVELS)
              : NO_POLICY;
          }),
        );
        return (
          <CellSelect
            value={mixed ? VARIES : (value ?? NO_POLICY)}
            disabled={stock.isLoading}
            onChange={(event) => {
              const selected = event.target.value;
              if (selected === VARIES) return;
              setPolicy(ids, (current) =>
                selected === NO_POLICY
                  ? null
                  : {
                      min: current?.min ?? null,
                      max: current?.max ?? null,
                      policyId:
                        selected === CUSTOM_LEVELS
                          ? null
                          : (selected as StockPolicyAssignment["policyId"]),
                    },
              );
            }}
          >
            {isAll && mixed && (
              <option value={VARIES}>{variesPlaceholder}</option>
            )}
            <option value={NO_POLICY}>
              {t("productForm.variantsTable.noPolicy")}
            </option>
            <option value={CUSTOM_LEVELS}>
              {t("productForm.variantsTable.customLevels")}
            </option>
            {stockPolicies.map((policy) => (
              <option key={policy.id} value={policy.id}>
                {policy.name}
              </option>
            ))}
          </CellSelect>
        );
      },
    },
    levelColumn("min", t("productForm.variantsTable.min")),
    levelColumn("max", t("productForm.variantsTable.max")),
  ];

  const columns = {
    price: priceColumns,
    codes: codeColumns,
    stock: stockColumns,
    policy: policyColumns,
  }[activeTab]();
  const allIds = variants.map((variant) => variant.id);
  const isColumnLocked = (columnId: string) =>
    !!chain?.isLocked(columnId) &&
    !columnId.startsWith("stock-") &&
    !(columnId.startsWith("policy") && !!policyLocation);

  return (
    <div>
      <Header>
        {variants.length > 0 ? (
          <Tabs role="tablist">
            {tabs.map((item) => (
              <TabButton
                key={item.value}
                type="button"
                role="tab"
                aria-selected={item.value === activeTab}
                $active={item.value === activeTab}
                onClick={() => setTab(item.value)}
              >
                {item.label}
              </TabButton>
            ))}
          </Tabs>
        ) : (
          <Empty>{t("productForm.variantsTable.empty")}</Empty>
        )}
        <Button
          type="button"
          $small
          $color="secondary"
          $variant="outlined"
          disabled={chain?.isLocked(null)}
          onClick={onManage}
        >
          {t("productForm.variantsTable.manage")}
        </Button>
      </Header>

      {variants.length > 0 && (
        <>
          {activeTab === "policy" && stock.locations.length > 1 && (
            <LocationPicker>
              <SelectInput
                small
                value={policyLocation?.id ?? ""}
                onChange={(event) =>
                  setPolicyLocationId(event.target.value as StockLocationId)
                }
              >
                {stock.locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.label
                      ? `${location.name} (${location.label})`
                      : location.name}
                  </option>
                ))}
              </SelectInput>
            </LocationPicker>
          )}
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>{t("productForm.name")}</th>
                  {columns.map((column) => (
                    <th key={column.id} data-numeric={column.numeric}>
                      {column.header}
                      {column.label && (
                        <HeaderLabel>{column.label}</HeaderLabel>
                      )}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {variants.length > 1 && (
                  <AllRow>
                    <td>{t("productForm.variantsTable.allVariants")}</td>
                    {columns.map((column) => (
                      <td key={column.id} data-numeric={column.numeric}>
                        <ReadOnlyFieldset disabled={isColumnLocked(column.id)}>
                          {column.cell(allIds, true)}
                        </ReadOnlyFieldset>
                      </td>
                    ))}
                    <td />
                  </AllRow>
                )}
                {variants.map((variant, index) => (
                  <tr key={variant.id}>
                    <td>
                      <NameInput
                        disabled={chain?.isLocked(null)}
                        value={variant.name ?? ""}
                        placeholder={
                          variantOptionsLabel(
                            { ...variant, name: null },
                            extraFieldKeys,
                          ) ??
                          `${t("productForm.variantsTable.variant")} ${index + 1}`
                        }
                        onChange={(event) =>
                          updateVariants([variant.id], (item) => ({
                            ...item,
                            name: event.target.value || null,
                          }))
                        }
                      />
                    </td>
                    {columns.map((column) => {
                      const content = (
                        <ReadOnlyFieldset disabled={isColumnLocked(column.id)}>
                          {column.cell([variant.id], false)}
                        </ReadOnlyFieldset>
                      );
                      return (
                        <td key={column.id} data-numeric={column.numeric}>
                          {chain
                            ? chain.renderCell(variant.id, column.id, content)
                            : content}
                        </td>
                      );
                    })}
                    <td>
                      <IconButton
                        type="button"
                        $color="danger"
                        disabled={chain?.isLocked(null)}
                        title={t("productForm.variantsTable.remove")}
                        onClick={() =>
                          onVariantsChange(
                            variants.filter((item) => item.id !== variant.id),
                          )
                        }
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
          <Hint>{t("productForm.variantsTable.hint")}</Hint>
        </>
      )}
    </div>
  );
};
